import React, { useState } from 'react';
import { Button } from './ui/button';
import { Heart, Share2, BookmarkPlus, BookmarkCheck, Download } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ShareModal from './ShareModal';

const AffirmationCard = ({ affirmation, onSave, onShare, isSaved = false }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const { toast } = useToast();

  const handleSave = () => {
    onSave(affirmation.id);
    if (!isSaved) {
      toast({
        title: "Affirmation Saved!",
        description: "Added to your Love Vault 💕",
        duration: 3000,
      });
    } else {
      toast({
        title: "Affirmation Removed",
        description: "Removed from your Self-Love Wall",
        duration: 3000,
      });
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const moodEmoji = {
    1: '😔',
    2: '😕', 
    3: '😐',
    4: '🙂',
    5: '😊'
  };

  return (
    <>
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-rose-200 transform transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{moodEmoji[affirmation.mood]}</span>
            <span className="text-sm font-medium text-rose-600 bg-rose-100 px-3 py-1 rounded-full">
              Daily Affirmation
            </span>
          </div>
          <Heart className="w-6 h-6 text-rose-400 fill-rose-400" />
        </div>

        <div className="mb-6">
          <blockquote className="text-lg leading-relaxed text-gray-800 font-medium mb-4 italic">
            "{affirmation.affirmation}"
          </blockquote>
          
          <div className="bg-rose-50 border-l-4 border-rose-400 p-4 rounded-r-lg">
            <p className="text-sm font-semibold text-rose-800 mb-1">💫 Action Step:</p>
            <p className="text-sm text-rose-700">{affirmation.action}</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex space-x-3">
            <Button
              onClick={handleSave}
              variant="outline"
              size="sm"
              className={`transition-all duration-300 ${
                isSaved 
                  ? 'border-rose-500 text-rose-700 bg-rose-50 hover:bg-rose-100'
                  : 'border-gray-300 text-gray-600 hover:border-rose-300 hover:text-rose-600 hover:bg-rose-50'
              }`}
            >
              {isSaved ? (
                <>
                  <BookmarkCheck className="w-4 h-4 mr-2" />
                  Saved
                </>
              ) : (
                <>
                  <BookmarkPlus className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>

            <Button
              onClick={handleShare}
              variant="outline"
              size="sm"
              className="border-gray-300 text-gray-600 hover:border-rose-300 hover:text-rose-600 hover:bg-rose-50 transition-all duration-300"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          <div className="text-xs text-gray-400">
            {new Date(affirmation.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        affirmation={affirmation}
      />
    </>
  );
};

export default AffirmationCard;